import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import LeafRectangleLoader from "../../../../../common/leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import "./DesktopTable.scss";
import LeafCircleLoader from "../../../../../common/leaf/LeafLoadingSkeleton/LeafCircleLoader/LeafCircleLoader";
import defaultContent from "../../../../../content/actionReports";

export const ActionReportTableLoader = ({ columns = 8, rows = 3 }: { columns?: number; rows?: number }) => {
  const { t } = useTranslation(["actionReports"]);
  const loadingLabel = t("tableColumnLoadingLabel", defaultContent.tableColumnLoadingLabel);
  const headerRef = useRef<HTMLTableRowElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const [columnWidths, setColumnWidths] = useState<number[]>([]);
  const [tableWidth, setTableWidth] = useState<number>(0);

  const measureColumnWidths = useCallback(() => {
    if (headerRef.current) {
      const cells = headerRef.current.querySelectorAll("th");
      const widths = Array.from(cells).map((cell) => cell.offsetWidth);
      setColumnWidths(widths);
    }
    if (tableRef.current) {
      setTableWidth(tableRef.current.offsetWidth);
    }
  }, []);

  useLayoutEffect(() => {
    measureColumnWidths();
  }, [measureColumnWidths, columns]);

  useEffect(() => {
    window.addEventListener("resize", measureColumnWidths);
    return () => window.removeEventListener("resize", measureColumnWidths);
  }, [measureColumnWidths]);

  const rowLoader = (row: number) => {
    return (
      <div className="ActionReportTableView__row" key={`ActionReportTableLoader--row${row}`}>
        <div className="ActionReportTableView__tile" style={{ width: tableWidth || "100%", paddingTop: "0rem" }}>
          <div className={`ActionReportTableView__row`} style={{ width: tableWidth || "100%", paddingTop: "0rem" }}>
            <div
              className="ActionReportTableView__cell ActionReportTableView__cell--profile"
              style={{ width: columnWidths[0] || "auto" }}
            >
              <div className="ActionReportTableView__aboInfo">
                <div className={`arProfileSection__badgeAndPhoto`}>
                  <div className="arProfileSection__dashedLine" aria-hidden="true">
                    <span className="arProfileSection__dash arProfileSection__dash--loading" />
                    <span className="arProfileSection__dash arProfileSection__dash--loading" />
                    <span className="arProfileSection__dash arProfileSection__dash--loading" />
                    <span className="arProfileSection__dash arProfileSection__dash--loading" />
                  </div>

                  <div className="arProfileSection__rankBadge arProfileSection__rankBadge--loading"></div>

                  <div className={`arProfileSection__photo`} style={{ marginLeft: "1rem" }}>
                    <LeafCircleLoader isLoading width="3.25rem" height="3.25rem" />
                  </div>
                </div>

                <LeafRectangleLoader isLoading width="8.25rem" height="1rem" margin="0" />
                <LeafRectangleLoader isLoading width="3.125rem" height="1rem" margin="0" />

                <div className="arProfileSection__actions" style={{ paddingLeft: "0rem" }}>
                  <LeafCircleLoader isLoading width="2.25rem" height="2.25rem" />
                  <LeafCircleLoader isLoading width="2.25rem" height="2.25rem" />
                  <LeafCircleLoader isLoading width="2.25rem" height="2.25rem" />
                </div>
              </div>
            </div>
            {Array(columns - 1)
              .fill(3)
              .map((_, col) => (
                <div
                  key={`ActionReportTableLoader--row--col${col}`}
                  className="ActionReportTableView__cell"
                  style={{ width: columnWidths[col + 1] || "auto" }}
                >
                  <LeafRectangleLoader isLoading width="3.125rem" height="1rem" margin="0" />
                </div>
              ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="ActionReportTableView">
      <table ref={tableRef} className="ActionReportTableView__table">
        <thead>
          <tr ref={headerRef} className="ActionReportTableView__header">
            {Array(columns)
              .fill(1)
              .map((_, index) => (
                <th
                  key={index}
                  className="ActionReportTableView__cell"
                  style={{ justifyItems: index > 0 ? "center" : "flex-start" }}
                >
                  <span className="sr-only">{loadingLabel}</span>
                  <LeafRectangleLoader isLoading width="6.25rem" height="1rem" margin="0" />
                </th>
              ))}
          </tr>
        </thead>
      </table>
      <div style={{ width: tableWidth || "100%" }}>
        {Array(rows)
          .fill(2)
          .map((_, row) => rowLoader(row))}
      </div>
    </div>
  );
};
